import { TCompanyForm } from "@/features/company/types/CompanyForm";
import { TCreateCompanyReqDto } from "@/api/services/main/company/dtos/create-company-req.dto";
import { TGetCompanyDto } from "@/api/services/main/company/dtos/get-company.dto";

// Normalize industry fields: shift data up if earlier fields are empty
type TIndustryPair = {
  industryId: number | null;
  classificationIds: number[] | null;
};

const normalizeIndustryFields = (data: TCompanyForm): TIndustryPair[] => {
  const pairs: TIndustryPair[] = [
    {
      industryId: data.companySpecificIndustryId1 ?? null,
      classificationIds: data.companySpecificBusinessClassificationIds1 ?? null,
    },
    {
      industryId: data.companySpecificIndustryId2 ?? null,
      classificationIds: data.companySpecificBusinessClassificationIds2 ?? null,
    },
    {
      industryId: data.companySpecificIndustryId3 ?? null,
      classificationIds: data.companySpecificBusinessClassificationIds3 ?? null,
    },
  ];

  const filledPairs = pairs.filter((pair) => pair.industryId !== null);

  while (filledPairs.length < 3) {
    filledPairs.push({ industryId: null, classificationIds: null });
  }

  return filledPairs;
};

export const convertCompanyFormDataReq = (data: TCompanyForm): TCreateCompanyReqDto => {
  let capital: string | null = null;
  if (data.capital) {
    capital = data.capital.replaceAll(",", "");
  }
  if (data.telNumber) {
    data.telNumber = data.telNumber;
  }
  if (data.faxNumber) {
    data.faxNumber = data.faxNumber;
  }
  if (data.contactPersonPhone) {
    data.contactPersonPhone = data.contactPersonPhone;
  }
  if (data.memo) {
    data.memo = normalizeTextarea(data.memo);
  }
  if (data.companyDescription) {
    data.companyDescription = normalizeTextarea(data.companyDescription);
  }
  const businessOffices =
    data.businessOffices?.map((office) => {
      return {
        ...office,
        telNumber: office.telNumber,
        faxNumber: office.faxNumber,
      };
    }) ?? [];
  const companyBranches =
    data.companyBranches?.map((companyBranch) => {
      return {
        ...companyBranch,
        telNumber: companyBranch.telNumber,
        faxNumber: companyBranch.faxNumber,
      };
    }) ?? [];

  const normalizedIndustry = normalizeIndustryFields(data);
  return {
    companyLogo: data.companyLogo ?? null,
    companyName: data.companyName,
    companyKanaName: data.companyKanaName ?? null,
    mainIndustryId: data.mainIndustryId ?? null,
    establishmentYear: data.establishmentYear ? String(Number(data.establishmentYear)) : null,
    numberOfEmployees: data.numberOfEmployees ?? null,
    capital: capital,
    representativeName: data.representativeName ?? null,
    representativePosition: data.representativePosition ?? null,
    telNumber: data.telNumber ?? null,
    faxNumber: data.faxNumber ?? null,
    contactEmail: data.contactEmail ?? null,
    websiteUrl: data.websiteUrl ?? null,
    zipCode: data.zipCode ? data.zipCode.replaceAll("-", "") : "",
    prefecture: data.prefecture ?? null,
    city: data.city ?? null,
    address: data.address ?? null,
    businessOffices: businessOffices,
    companyBranches: companyBranches,
    companyDescription: data.companyDescription ?? null,
    memo: data.memo ?? null,
    managementNumber: data.managementNumber ?? null,
    contractUrl: data.contractUrl ?? null,
    contractPaths: data.contractPaths ?? null,
    contractFiles: data.contractFiles ?? null,
    companyLogoFile: data.companyLogoFile ?? null,
    contactPersonName: data.contactPersonName ?? null,
    contactPersonEmail: data.contactPersonEmail ?? null,
    contactPersonPhone: data.contactPersonPhone ?? null,
    contactPersonJobTitle: data.contactPersonJobTitle ?? null,
    companySpecificIndustryId1: normalizedIndustry[0].industryId,
    companySpecificBusinessClassificationIds1: normalizedIndustry[0].classificationIds,
    companySpecificIndustryId2: normalizedIndustry[1].industryId,
    companySpecificBusinessClassificationIds2: normalizedIndustry[1].classificationIds,
    companySpecificIndustryId3: normalizedIndustry[2].industryId,
    companySpecificBusinessClassificationIds3: normalizedIndustry[2].classificationIds,
  };
};

export const convertResToFormData = (data: TGetCompanyDto): Partial<TCompanyForm> | undefined => {
  if (!data) return undefined;
  const businessOffices =
    data.businessOffices?.map((office) => {
      return {
        ...office,
        telNumber: office.telNumber || null,
        faxNumber: office.faxNumber || null,
      };
    }) ?? [];
  const companyBranches =
    data.companyBranches?.map((companyBranch) => {
      return {
        ...companyBranch,
        telNumber: companyBranch.telNumber || null,
        faxNumber: companyBranch.faxNumber || null,
      };
    }) ?? [];
  return {
    companyLogo: data.companyLogo,
    companyName: data.companyName,
    companyKanaName: data.companyKanaName,
    mainIndustryId: data.mainIndustryId,
    establishmentYear: data.establishmentYear,
    numberOfEmployees: data.numberOfEmployees,
    capital: data.capital,
    representativeName: data.representativeName,
    representativePosition: data.representativePosition,
    telNumber: data.telNumber || null,
    faxNumber: data.faxNumber || null,
    contactEmail: data.contactEmail,
    websiteUrl: data.websiteUrl,
    zipCode: data.zipCode,
    prefecture: data.prefecture,
    city: data.city,
    address: data.address,
    companyDescription: data.companyDescription,
    memo: data.memo,
    managementNumber: data.managementNumber,
    businessOffices: businessOffices,
    companyBranches: companyBranches,
    contractUrl: data.contractUrl,
    contractPaths: data.contractPaths,
    contactPersonName: data.contactPersonName,
    contactPersonEmail: data.contactPersonEmail,
    contactPersonPhone: data.contactPersonPhone,
    contactPersonJobTitle: data.contactPersonJobTitle,
    mainIndustryName: data?.mainIndustry?.mainIndustry,
    jobs: data?.jobs ?? [],
    isArchive: data.isArchive,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
    companySpecificIndustryId1: data.industryClassification?.companySpecificIndustryId1 ?? null,
    companySpecificBusinessClassificationIds1: data.industryClassification?.companySpecificBusinessClassificationIds1 ?? [],
    companySpecificIndustryId2: data.industryClassification?.companySpecificIndustryId2 ?? null,
    companySpecificBusinessClassificationIds2: data.industryClassification?.companySpecificBusinessClassificationIds2 ?? [],
    companySpecificIndustryId3: data.industryClassification?.companySpecificIndustryId3 ?? null,
    companySpecificBusinessClassificationIds3: data.industryClassification?.companySpecificBusinessClassificationIds3 ?? [],
  };
};

const normalizeTextarea = (text: string): string => {
  if (!text) return "";

  return text
    .replace(/\t+/g, " ")
    .replace(/ {2,}/g, " ")
    .replace(/\n{2,}/g, "\n")
    .trim();
};
